'use client';
import { useRef, useCallback, useEffect } from 'react';

/**
 * useGameAudio — synthesises all ViNCi game sounds via the Web Audio API.
 * No external assets required; every sound is generated procedurally.
 */
export function useGameAudio() {
  const ctxRef = useRef<AudioContext | null>(null);

  // Lazily create (or resume) the AudioContext on first user interaction
  const getCtx = useCallback((): AudioContext | null => {
    if (typeof window === 'undefined') return null;
    if (!ctxRef.current) {
      ctxRef.current = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    }
    if (ctxRef.current.state === 'suspended') {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      ctxRef.current?.close();
    };
  }, []);

  // ─── Dice Roll ────────────────────────────────────────────────────────────
  const playDiceRoll = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;

    const now = ctx.currentTime;
    const duration = 0.8;

    // Rattling noise bursts — simulate dice tumbling
    for (let i = 0; i < 6; i++) {
      const bufferSize = ctx.sampleRate * 0.06;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let j = 0; j < bufferSize; j++) {
        data[j] = (Math.random() * 2 - 1) * Math.pow(1 - j / bufferSize, 1.5);
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 800 + Math.random() * 1200;
      filter.Q.value = 2;

      const gain = ctx.createGain();
      const startTime = now + i * (duration / 6);
      gain.gain.setValueAtTime(0.35, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.06);

      source.connect(filter);
      filter.connect(gain);
      gain.connect(ctx.destination);
      source.start(startTime);
      source.stop(startTime + 0.07);
    }

    // Final "clack" impact
    const clackOsc = ctx.createOscillator();
    const clackGain = ctx.createGain();
    clackOsc.type = 'square';
    clackOsc.frequency.setValueAtTime(220, now + duration);
    clackOsc.frequency.exponentialRampToValueAtTime(80, now + duration + 0.08);
    clackGain.gain.setValueAtTime(0.4, now + duration);
    clackGain.gain.exponentialRampToValueAtTime(0.001, now + duration + 0.12);
    clackOsc.connect(clackGain);
    clackGain.connect(ctx.destination);
    clackOsc.start(now + duration);
    clackOsc.stop(now + duration + 0.13);
  }, [getCtx]);

  // ─── Piece Placement ──────────────────────────────────────────────────────
  const playPiecePlacement = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Soft "click" thud
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, now);
    osc.frequency.exponentialRampToValueAtTime(120, now + 0.08);
    gain.gain.setValueAtTime(0.5, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.13);

    // Subtle high-frequency tick
    const tick = ctx.createOscillator();
    const tickGain = ctx.createGain();
    tick.type = 'triangle';
    tick.frequency.value = 1800;
    tickGain.gain.setValueAtTime(0.15, now);
    tickGain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
    tick.connect(tickGain);
    tickGain.connect(ctx.destination);
    tick.start(now);
    tick.stop(now + 0.05);
  }, [getCtx]);

  // ─── Formation Detected ───────────────────────────────────────────────────
  const playFormationDetected = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Rising arpeggio — three ascending notes
    const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;

      const t = now + i * 0.12;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.35, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.25);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.26);
    });

    // Shimmer overtone
    const shimmer = ctx.createOscillator();
    const shimmerGain = ctx.createGain();
    shimmer.type = 'triangle';
    shimmer.frequency.value = 1567.98; // G6
    shimmerGain.gain.setValueAtTime(0, now + 0.24);
    shimmerGain.gain.linearRampToValueAtTime(0.18, now + 0.28);
    shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    shimmer.connect(shimmerGain);
    shimmerGain.connect(ctx.destination);
    shimmer.start(now + 0.24);
    shimmer.stop(now + 0.61);
  }, [getCtx]);

  // ─── Victory ─────────────────────────────────────────────────────────────
  const playVictory = useCallback(() => {
    const ctx = getCtx();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Fanfare melody — ascending triumphant phrase
    const melody: [number, number, number][] = [
      // [frequency, startOffset, duration]
      [523.25, 0.0, 0.18],   // C5
      [659.25, 0.18, 0.18],  // E5
      [783.99, 0.36, 0.18],  // G5
      [1046.5, 0.54, 0.36],  // C6 (held)
      [987.77, 0.9, 0.18],   // B5
      [1046.5, 1.08, 0.5],   // C6 (final)
    ];

    melody.forEach(([freq, offset, dur]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;

      const t = now + offset;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.4, t + 0.02);
      gain.gain.setValueAtTime(0.4, t + dur - 0.04);
      gain.gain.exponentialRampToValueAtTime(0.001, t + dur);

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + dur + 0.01);
    });

    // Harmonic pad underneath
    const padFreqs = [261.63, 329.63, 392.0]; // C4, E4, G4
    padFreqs.forEach((freq) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.12, now + 0.1);
      gain.gain.setValueAtTime(0.12, now + 1.4);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 1.8);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 1.81);
    });
  }, [getCtx]);

  return { playDiceRoll, playPiecePlacement, playFormationDetected, playVictory };
}
